import {
  Alert,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  alpha,
  useTheme,
  useMediaQuery,
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  AudioDataSourceItemPlayer,
  JsonDataSourceItemViewer,
  CsvDataSourceItemViewer,
  ImageDataSourceItemViewer,
  PdfDataSourceItemViewer,
  VideoDataSourceViewer,
  XmlDataSourceItemViewer,
  ExcelDataSourceItemViewer,
} from '../viewers';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from 'material-ui-confirm';
import { useApiContext } from '@/contexts/apiContext';
import { useNotificationContext } from '@/contexts/notificationContext';
import ProgressIndicator from '../progressIndicator';
import ErrorMessage from '../errorMessage';
import EventDataViewer from '../viewers/eventDataViewer';
import SelectField from '../selectField';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TimelineIcon from '@mui/icons-material/Timeline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { eventNameEquals } from '@/utils';

type Props = { agentInstance: any; item: any; maxHeight: number };

/** Derive a human-readable label for a mime type */
const mimeLabel = (mimeType?: string): string => {
  if (!mimeType) return 'File';
  const lower = mimeType.toLowerCase();
  if (lower.startsWith('video')) return 'Video';
  if (lower.startsWith('audio')) return 'Audio';
  if (lower.startsWith('image')) return 'Image';
  if (lower.startsWith('application/json')) return 'JSON';
  if (lower.startsWith('application/xml') || lower.startsWith('text/xml')) return 'XML';
  if (lower.startsWith('application/csv') || lower.startsWith('text/csv')) return 'CSV';
  if (lower.startsWith('application/pdf')) return 'PDF';
  return mimeType.split('/')[1]?.toUpperCase() || 'File';
};

const HEADER_H = 44;

const ViewDataSourceItem = ({ agentInstance, item, maxHeight }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const apiContext = useApiContext();
  const confirm = useConfirm();

  const [loadingGroup, setLoadingGroup] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const { toast } = useNotificationContext();
  const [dataSourceItems, setDataSourceItems] = useState<any[]>([]);
  const [dataSourceItem, setDataSourceItem] = useState<any>();
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>();
  const [processingResults, setProcessingResults] = useState<any[]>([]);
  const [error, setError] = useState<any>();
  const [suppressEvents, setSuppressEvents] = useState<boolean>(false);

  const handleDeleteEvent = async (ev: any) => {
    if (!ev?.id) return;
    try {
      await confirm({
        title: 'Delete Event Data',
        description: `Are you sure you want to permanently delete this event record (ID: ${ev.id})? This action cannot be undone.`,
        confirmationText: 'Delete',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });

      if (ev.sourceEntityType === 'dataSourceInstance' || ev.sourceEntityType === 'dataSourceDef') {
        const defId = ev.sourceEntityDefId || dataSourceItem?.dataSourceDefId || item?.dataSourceDefId;
        const instId = ev.sourceEntityId || dataSourceItem?.dataSourceInstanceId || item?.dataSourceInstanceId;
        await apiContext.deleteDataSourceItemEventData(defId, instId, ev.id);
      } else if (ev.sourceEntityType === 'userAppInstance' || ev.sourceEntityType === 'userAppDef') {
        const defId = ev.sourceEntityDefId;
        const instId = ev.sourceEntityId;
        await apiContext.deleteUserAppInstanceEventData(defId, instId, ev.id);
      } else {
        const defId = ev.sourceEntityDefId || agentInstance?.agentDefId;
        const instId = ev.sourceEntityId || agentInstance?.id;
        await apiContext.deleteAgentInstanceEventData(defId, instId, ev.id);
      }

      setProcessingResults((prev) => prev.filter((e) => e.id !== ev.id));
      if (selectedEventId === ev.id) {
        setSelectedEventId(undefined);
      }
    } catch (err: any) {
      if (err) {
        console.error('Failed to delete event data:', err);
        setError(err?.response?.data?.message || err?.message || 'Failed to delete event data.');
      }
    }
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch group items if applicable
  useEffect(() => {
    const getGroupDataSourceItems = async () => {
      if (!item?.dataSourceItemGroup) return;
      setError(null);
      try {
        setLoadingGroup(true);
        const items = await apiContext.getGroupDataSourceItems(
          item.dataSourceDefId,
          item.dataSourceInstanceId,
          item.dataSourceItemGroup,
        );
        const mappedItems = items.map((i) => ({
          id: i.id,
          dataSourceDefId: i.dataSourceDefId,
          dataSourceInstanceId: i.dataSourceInstanceId,
          dataSourceItemId: i.id,
          itemName: i.itemName,
          mimeType: i.mimeType,
          status: i.status,
          error: i.error,
        }));
        setDataSourceItems(mappedItems);
        if (mappedItems.length > 0) {
          setDataSourceItem(mappedItems[0]);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoadingGroup(false);
      }
    };

    if (item?.dataSourceItemId) {
      const directItem = {
        id: item.id,
        dataSourceDefId: item.dataSourceDefId,
        dataSourceInstanceId: item.dataSourceInstanceId,
        dataSourceItemId: item.dataSourceItemId,
        itemName: item.itemName,
        mimeType: item.mimeType,
        status: item.status,
        error: item.error,
      };
      setDataSourceItem(directItem);
      setDataSourceItems([directItem]);
    } else if (item?.dataSourceItemGroup) {
      getGroupDataSourceItems();
    }
  }, [item]);

  // Target item identifiers for trace fetching
  const targetItemId = dataSourceItem?.dataSourceItemId || dataSourceItem?.id || item?.dataSourceItemId || item?.id;
  const targetDefId = dataSourceItem?.dataSourceDefId || item?.dataSourceDefId;
  const targetInstId = dataSourceItem?.dataSourceInstanceId || item?.dataSourceInstanceId;

  // Fetch ALL events for the item (Global Trace)
  const getGlobalProcessingResults = useCallback(async () => {
    if (!targetItemId || !targetDefId || !targetInstId) return;
    setError(null);
    try {
      setEventsLoading(true);
      const results = await apiContext.getDataSourceItemEventsData(targetDefId, targetInstId, targetItemId);
      setProcessingResults(results || []);
    } catch (error) {
      setError(error);
    } finally {
      setEventsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetItemId, targetDefId, targetInstId]);

  useEffect(() => {
    getGlobalProcessingResults();
  }, [getGlobalProcessingResults]);

  const handleExecuteItem = async () => {
    if (!dataSourceItem || !agentInstance?.agentDefId || !agentInstance?.id) return;
    setIsExecuting(true);
    setError(null);
    const itemLabel = dataSourceItem.itemName || dataSourceItem.dataSourceItemId || dataSourceItem.id;
    try {
      const res = await apiContext.executeAgentInstance(agentInstance.agentDefId, agentInstance.id, {
        pipelineInstanceId: item?.pipelineInstanceId || dataSourceItem?.pipelineInstanceId,
        dataSourceDefId: dataSourceItem.dataSourceDefId,
        dataSourceInstanceId: dataSourceItem.dataSourceInstanceId,
        dataSourceItemId: dataSourceItem.dataSourceItemId || dataSourceItem.id,
        itemName: dataSourceItem.itemName,
        suppressEvents,
      });
      const isFailed =
        res?.status === 'failed' || Boolean(res?.error) || (res?.failedCount > 0 && res?.successCount === 0);
      const errMsg = res?.error || res?.items?.find((i: any) => i.error)?.error;

      if (isFailed) {
        const finalMsg = errMsg || `Agent execution failed for "${itemLabel}".`;
        setError(finalMsg);
        toast({
          message: finalMsg,
          type: 'error',
        });
      } else {
        toast({
          message: `Agent executed successfully for "${itemLabel}"${suppressEvents ? ' (this step only)' : ''}`,
          type: 'success',
        });
      }
      await getGlobalProcessingResults();
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.error ||
        err?.response?.data?.items?.find((i: any) => i.error)?.error ||
        err?.response?.data?.message ||
        err?.message ||
        `Agent execution failed for "${itemLabel}".`;
      setError(errMsg);
      toast({
        message: errMsg,
        type: 'error',
      });
      await getGlobalProcessingResults();
    } finally {
      setIsExecuting(false);
    }
  };

  // Process events to identify superseded errors
  const processedEvents = useMemo(() => {
    const completedSources = new Set<string>();

    const sortedEvents = [...processingResults].sort(
      (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime(),
    );

    return sortedEvents.map((r) => {
      const sourceKey = `${r.sourceEntityType}-${r.sourceEntityId}`;
      const isSuccess = eventNameEquals(r.eventName, 'ItemProcessingComplete') || eventNameEquals(r.eventName, 'ItemGroupProcessingComplete');
      const isError =
        r.eventPayload?.payloadType === 'ERROR' ||
        r.eventPayload?.payloadType === 'ERROR_STRING' ||
        r.eventName?.toLowerCase().includes('error') ||
        r.eventName?.toLowerCase().includes('fail');

      if (isSuccess) {
        completedSources.add(sourceKey);
      }

      const isSuperseded = isError && completedSources.has(sourceKey);
      return { ...r, isError, isSuperseded };
    });
  }, [processingResults]);

  // Segregate events: current agent vs rest of the pipeline
  const { appEvents, otherEvents } = useMemo(() => {
    const agentEvents = processedEvents.filter(
      (r) => r.sourceEntityType === 'agentInstance' || r.sourceEntityType === 'agentDef',
    );

    const currentAgentEvents = agentEvents.filter(
      (r) =>
        agentInstance?.id?.toString() === r.sourceEntityId?.toString() ||
        agentInstance?.agentDefId?.toString() === r.sourceEntityDefId?.toString(),
    );

    const otherAgentEvents = agentEvents.filter((r) => !currentAgentEvents.includes(r));
    return { appEvents: currentAgentEvents, otherEvents: otherAgentEvents };
  }, [processedEvents, agentInstance]);

  const totalEvents = appEvents.length + otherEvents.length;

  const errorCount = useMemo(() => {
    let count = processedEvents.filter((r) => r.isError && !r.isSuperseded).length;
    if (dataSourceItem?.status === 'FAILED') {
      count += 1;
    }
    return count;
  }, [processedEvents, dataSourceItem?.status]);

  const isParentDocument = useMemo(() => {
    const meta = dataSourceItem?.dataSourceDef?.metadata || {};
    if (meta.hasParentChildItems === true && meta.publishParentItemEvents === false) {
      return !dataSourceItem?.parentItemName;
    }
    return false;
  }, [dataSourceItem]);

  const selectFieldHeight = dataSourceItems.length > 1 ? 52 : 0;
  const viewerProps = {
    dataSourceDefId: dataSourceItem?.dataSourceDefId,
    dataSourceInstanceId: dataSourceItem?.dataSourceInstanceId,
    dataSourceItemId: dataSourceItem?.dataSourceItemId || dataSourceItem?.id,
    filename: dataSourceItem?.itemName,
    maxHeight: isMobile ? 550 - HEADER_H - 26 - selectFieldHeight : maxHeight - HEADER_H - 26 - selectFieldHeight,
    showLabel: false,
    eventId: selectedEventId,
    parentItemName: dataSourceItem?.parentItemName,
    pipelineInstanceId: dataSourceItem?.pipelineInstanceId || item?.pipelineInstanceId,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: maxHeight },
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      {error && (
        <Box sx={{ p: 1, pb: 0 }}>
          <ErrorMessage error={error} onClose={() => setError(null)} />
        </Box>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: { xs: 'visible', md: 'hidden' },
          bgcolor: 'background.default',
        }}
      >
        {/* ── LEFT: Content Viewer ───────────────────────────── */}
        <Box
          sx={{
            flex: { xs: 'none', md: isParentDocument ? '1 1 100%' : '1 1 55%' },
            height: { xs: '550px', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          {/* Section header */}
          <Box
            sx={{
              height: HEADER_H,
              minHeight: HEADER_H,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              gap: 1.5,
              borderBottom: 1,
              borderRight: { xs: 0, md: 1 },
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <InsertDriveFileIcon fontSize="small" sx={{ color: 'primary.main', opacity: 0.8 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, lineHeight: 1 }} noWrap>
              {dataSourceItem?.itemName || 'Content'}
            </Typography>
            {processingResults.length > 0 && (
              <Select
                value={selectedEventId || 'latest'}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedEventId(val === 'latest' ? undefined : (val as string));
                }}
                size="small"
                sx={{
                  height: 26,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  maxWidth: 180,
                  bgcolor: 'background.paper',
                  '& .MuiSelect-select': { py: 0.5, pr: 3, pl: 1 },
                }}
              >
                <MenuItem value="latest" sx={{ fontSize: '0.72rem' }}>
                  Latest Ingested
                </MenuItem>
                {processingResults.map((event) => {
                  const { sourceEntityName, sourceEntityType, dateTime } = event;
                  const label = sourceEntityName || t(`common.entityType.${sourceEntityType}`);
                  const timeStr = dateTime
                    ? new Date(dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '';
                  return (
                    <MenuItem key={event.id} value={event.id} sx={{ fontSize: '0.72rem' }}>
                      {label} ({timeStr})
                    </MenuItem>
                  );
                })}
              </Select>
            )}
            {dataSourceItem?.mimeType && (
              <Chip
                label={mimeLabel(dataSourceItem.mimeType)}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
              />
            )}
            <Tooltip
              title={
                suppressEvents
                  ? 'Run this step only: Executes this step without triggering subsequent steps in the pipeline.'
                  : 'Run and continue: Executes this step and automatically triggers subsequent steps in the pipeline.'
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={suppressEvents}
                    onChange={(e) => setSuppressEvents(e.target.checked)}
                    disabled={isExecuting}
                    sx={{
                      p: 0.25,
                      '&.Mui-checked': { color: 'primary.main' },
                      '& .MuiSvgIcon-root': { fontSize: 16 },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: suppressEvents ? 'primary.main' : 'text.secondary',
                      userSelect: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Run this step only
                  </Typography>
                }
                sx={{ mr: 0.5, ml: 0 }}
              />
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={
                isExecuting ? <CircularProgress size={12} color="inherit" /> : <PlayArrowIcon sx={{ fontSize: 15 }} />
              }
              disabled={isExecuting}
              onClick={handleExecuteItem}
              sx={{
                height: 24,
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'none',
                px: 1,
                borderRadius: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {isExecuting ? 'Running...' : 'Run Agent'}
            </Button>
          </Box>

          {/* Viewer body */}
          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              p: 1.5,
              borderRight: { xs: 0, md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Group item selector */}
            {dataSourceItems.length > 1 && (
              <Box sx={{ mb: 1.5 }}>
                <SelectField
                  label="Item"
                  required
                  options={dataSourceItems.map((i) => ({ value: i.id, label: i.itemName }))}
                  value={dataSourceItem?.id}
                  onChange={(id) => {
                    const selected = dataSourceItems.find((i) => i.id === id);
                    setDataSourceItem(selected);
                  }}
                />
              </Box>
            )}

            <Paper
              elevation={0}
              sx={{
                flex: 1,
                borderRadius: '10px',
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {selectedEventId && (
                <Alert
                  severity="info"
                  variant="filled"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setSelectedEventId(undefined)}
                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', py: 0 }}
                    >
                      Reset to Latest
                    </Button>
                  }
                  sx={{
                    borderRadius: 0,
                    py: 0.25,
                    px: 2,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Viewing historical trace event payload
                </Alert>
              )}
              {dataSourceItem?.mimeType?.startsWith('video') ? (
                <VideoDataSourceViewer {...viewerProps} mimeType={dataSourceItem.mimeType} />
              ) : dataSourceItem?.mimeType?.startsWith('audio') ? (
                <AudioDataSourceItemPlayer {...viewerProps} mimeType={dataSourceItem.mimeType} />
              ) : dataSourceItem?.mimeType?.startsWith('image') ? (
                <ImageDataSourceItemViewer {...viewerProps} mimeType={dataSourceItem.mimeType} />
              ) : dataSourceItem?.mimeType?.toLowerCase()?.startsWith('application/json') ? (
                <JsonDataSourceItemViewer {...viewerProps} />
              ) : dataSourceItem?.mimeType?.toLowerCase()?.startsWith('application/xml') ||
                dataSourceItem?.mimeType?.toLowerCase()?.startsWith('text/xml') ||
                dataSourceItem?.mimeType?.toLowerCase() === 'application/atom+xml' ? (
                <XmlDataSourceItemViewer {...viewerProps} />
              ) : dataSourceItem?.mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                dataSourceItem?.mimeType === 'application/vnd.ms-excel' ||
                dataSourceItem?.itemName?.toLowerCase()?.endsWith('.xlsx') ||
                dataSourceItem?.itemName?.toLowerCase()?.endsWith('.xls') ? (
                <ExcelDataSourceItemViewer {...viewerProps} />
              ) : dataSourceItem?.mimeType?.toLowerCase()?.startsWith('application/csv') ||
                dataSourceItem?.mimeType?.toLowerCase()?.startsWith('text/csv') ||
                dataSourceItem?.itemName?.toLowerCase()?.endsWith('.csv') ? (
                <CsvDataSourceItemViewer {...viewerProps} separator="," />
              ) : (
                <PdfDataSourceItemViewer {...viewerProps} />
              )}
            </Paper>
          </Box>
        </Box>

        {/* ── RIGHT: Events Traceability ────────────────────── */}
        {!isParentDocument && (
          <Box
            sx={{
              flex: { xs: 'none', md: '1 1 45%' },
              height: { xs: 'auto', md: '100%' },
              display: 'flex',
              flexDirection: 'column',
              overflow: { xs: 'visible', md: 'hidden' },
              minWidth: 0,
            }}
          >
            {/* Section header */}
            <Box
              sx={{
                height: HEADER_H,
                minHeight: HEADER_H,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                gap: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.background.paper, 0.8),
              }}
            >
              <TimelineIcon
                fontSize="small"
                sx={{ color: errorCount > 0 ? 'error.main' : 'secondary.main', opacity: 0.8 }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ flex: 1, color: errorCount > 0 ? 'error.main' : 'inherit' }}
              >
                Processing Trace
              </Typography>
              {errorCount > 0 && (
                <Chip
                  label={`${errorCount} ${errorCount === 1 ? 'Error' : 'Errors'}`}
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                />
              )}
              {totalEvents > 0 && (
                <Badge
                  badgeContent={totalEvents}
                  color={errorCount > 0 ? 'default' : 'primary'}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}
                >
                  <Box sx={{ width: 16, height: 16 }} />
                </Badge>
              )}
            </Box>

            {/* Events body */}
            <Box sx={{ flex: 1, overflowY: { xs: 'visible', md: 'auto' }, p: 1.5 }}>
              {eventsLoading && processingResults.length === 0 ? (
                <ProgressIndicator />
              ) : (
                <Stack spacing={1.5}>
                  {error && <ErrorMessage error={error} />}

                  {dataSourceItem?.status === 'FAILED' && (
                    <ErrorMessage error={dataSourceItem.error || 'Item processing failed.'} />
                  )}

                  {/* Current Agent Events */}
                  {appEvents.length > 0 && (
                    <Stack spacing={1.5}>
                      {appEvents.map((r, idx) => (
                        <EventDataViewer
                          key={r.id || idx}
                          eventData={r}
                          isSuperseded={r.isSuperseded}
                          maxHeight={maxHeight - HEADER_H}
                          defaultExpanded={idx === 0}
                          isAppSource={true}
                          badgeLabel="Agent Processing"
                          onDelete={handleDeleteEvent}
                        />
                      ))}
                    </Stack>
                  )}

                  {/* Separator */}
                  {appEvents.length > 0 && otherEvents.length > 0 && (
                    <Box sx={{ py: 1 }}>
                      <Divider>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 1 }}
                        >
                          Other pipeline processing
                        </Typography>
                      </Divider>
                    </Box>
                  )}

                  {/* Other / Global Trace Events */}
                  {otherEvents.length > 0 && (
                    <Box>
                      {appEvents.length === 0 && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, px: 0.5 }}>
                          <TravelExploreIcon fontSize="small" color="action" />
                          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                            Global Trace Events
                          </Typography>
                          <Chip
                            label={otherEvents.length}
                            size="small"
                            color="default"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Stack>
                      )}
                      <Stack spacing={1.5}>
                        {otherEvents.map((r, idx) => (
                          <EventDataViewer
                            key={r.id || idx}
                            eventData={r}
                            isSuperseded={r.isSuperseded}
                            maxHeight={maxHeight - HEADER_H}
                            defaultExpanded={appEvents.length === 0 && idx === 0}
                            isAppSource={false}
                            onDelete={handleDeleteEvent}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {appEvents.length === 0 && otherEvents.length === 0 && !eventsLoading && (
                    <Box
                      sx={{
                        mt: 4,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        opacity: 0.5,
                      }}
                    >
                      <SmartToyIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">
                        No processing events found for this item.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default ViewDataSourceItem;
